import type { Origin } from '@pkg/types/surpriseBag';

type OriginWithPlaceId = Origin & { place_id?: string };

/**
 * Composable for handling search URL parameters
 * Used to preserve search state when navigating between LocationFinder and SurpriseBag pages
 */
export function useSearchParams() {
  const route = useRoute();
  const router = useRouter();

  const searchParams = computed(() => ({
    search: route.query.search as string | undefined,
    lat: route.query.lat as string | undefined,
    lng: route.query.lng as string | undefined,
    search_context: route.query.search_context as string | undefined,
  }));

  const hasSearchParams = computed(() =>
    !!(searchParams.value.search && searchParams.value.lat && searchParams.value.lng),
  );

  /**
   * Get location data from URL params
   */
  const getLocationFromUrl = (): OriginWithPlaceId | null => {
    const { search, lat, lng } = searchParams.value;
    if (!search || !lat || !lng) {
      return null;
    }

    return {
      displayName: search,
      center: {
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
      },
    } as OriginWithPlaceId;
  };

  const saveLocationToUrl = (locationData: OriginWithPlaceId) => {
    const { displayName, center } = locationData;
    router.replace({
      query: {
        ...route.query,
        ...(displayName && { search: displayName }),
        ...(center?.latitude && center?.longitude && {
          lat: center.latitude.toString(),
          lng: center.longitude.toString(),
        }),
      },
    });
  };

  /**
   * Build a path with search params
   * If locationData is provided, uses that; otherwise reads from current URL params
   */
  const buildPathWithSearchParams = (
    basePath: string,
    locationData?: OriginWithPlaceId,
    options?: { searchContext?: 'header' },
  ): string => {
    let search: string | undefined;
    let lat: string | undefined;
    let lng: string | undefined;

    if (locationData) {
      search = locationData.displayName;
      lat = locationData.center?.latitude?.toString();
      lng = locationData.center?.longitude?.toString();
    } else {
      ({ search, lat, lng } = searchParams.value);
    }

    const merged = {
      ...route.query,
      ...(search && lat && lng && { search, lat, lng }),
      ...(options?.searchContext && { search_context: options.searchContext }),
    };
    const entries = Object.entries(merged).filter(([, v]) => v);
    return entries.length ? `${basePath}?${new URLSearchParams(entries as [string, string][]).toString()}` : basePath;
  };

  const clearSearchContextFromUrl = () => {
    const { search_context: _, ...rest } = route.query;
    router.replace({ query: rest });
  };

  return {
    searchParams,
    hasSearchParams,
    getLocationFromUrl,
    saveLocationToUrl,
    buildPathWithSearchParams,
    clearSearchContextFromUrl,
  };
}
